import { useState, type ReactNode } from "react";
import {
  ContextMenu,
  ContextMenuCheckboxItem,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuShortcut,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { useSettingsHub } from "@/shell/settings-hub";
import { useOwnWork, usePlanSurface } from "@/features/access/use-plan-access";
import { openLinkedTasks } from "@/features/tasks/links";
import { tasksLinkedTo } from "@/features/tasks/task-query";
import { modKey } from "@/lib/format";
import { useProjectStore } from "@/store/project-store";

/**
 * Right-click menu shared by the WBS grid and the Gantt. The row under the pointer is resolved from
 * `data-row-id` on an ancestor element; if it is not already part of the selection it becomes the selection.
 */
export function TaskContextMenu({ children }: { children: ReactNode }) {
  const [target, setTarget] = useState<string | null>(null);

  const onContextMenu = (e: React.MouseEvent) => {
    const el = (e.target as HTMLElement).closest<HTMLElement>("[data-row-id]");
    const id = el?.dataset.rowId ?? null;
    setTarget(id);
    const s = useProjectStore.getState();
    if (id && !s.selection.includes(id)) s.select([id], id);
    if (!id) s.select([], null);
  };

  return (
    <ContextMenu modal={false}>
      <ContextMenuTrigger asChild>
        <div onContextMenu={onContextMenu} className="contents">
          {children}
        </div>
      </ContextMenuTrigger>
      <MenuBody id={target} />
    </ContextMenu>
  );
}

function MenuBody({ id }: { id: string | null }) {
  const selection = useProjectStore((s) => s.selection);
  const ui = useProjectStore((s) => s.ui);
  const surface = usePlanSurface();
  const own = useOwnWork();
  const s = useProjectStore.getState;
  const task = id ? s().project.tasks.find((t) => t.id === id) : undefined;
  const sched = id ? s().derived.schedule.tasks.get(id) : undefined;
  const many = selection.length > 1;
  const collapsed = id ? s().collapsed.has(id) : false;
  const linkedCount = id ? tasksLinkedTo(id, s().project.executionTasks, s().derived.schedule).length : 0;

  if (!id || !task || !sched) {
    // empty area
    return (
      <ContextMenuContent className="w-56">
        {surface.editWbs && (
          <>
            <ContextMenuItem onSelect={() => s().addSibling(null)}>
              맨 끝에 작업 추가
              <ContextMenuShortcut>{modKey}↩</ContextMenuShortcut>
            </ContextMenuItem>
            <ContextMenuItem onSelect={() => s().addSibling(null, { duration: 0, name: "마일스톤" })}>맨 끝에 마일스톤 추가</ContextMenuItem>
            <ContextMenuSeparator />
          </>
        )}
        <ContextMenuItem onSelect={() => s().setCollapsedAll(false)}>모두 펼치기</ContextMenuItem>
        <ContextMenuItem onSelect={() => s().setCollapsedAll(true)}>모두 접기</ContextMenuItem>
        {surface.editProgress && (
          <>
            <ContextMenuSeparator />
            <ContextMenuItem onSelect={() => s().openDialog("status-update")}>진척 업데이트…</ContextMenuItem>
          </>
        )}
        <ContextMenuSeparator />
        <ContextMenuItem onSelect={() => useSettingsHub.getState().open("app")}>앱 설정</ContextMenuItem>
      </ContextMenuContent>
    );
  }

  const setProgress = (v: number) => {
    const ids = (selection.length ? selection : [id]).filter((taskId) => {
      const row = s().project.tasks.find((item) => item.id === taskId);
      return !!row && own.scheduleOwn(row);
    });
    const leafIds = ids.filter((x) => !s().derived.schedule.tasks.get(x)?.isSummary);
    if (!leafIds.length) return;
    s().updateTasks(
      leafIds.map((x) => ({ id: x, patch: { percentComplete: v } })),
      `${leafIds.length}개 작업 진척 → ${v}%`,
    );
  };

  return (
    <ContextMenuContent className="w-60">
      {surface.editWbs && (
        <>
          <ContextMenuItem onSelect={() => s().addSibling(id)}>
            아래에 작업 추가
            <ContextMenuShortcut>{modKey}↩</ContextMenuShortcut>
          </ContextMenuItem>
          <ContextMenuItem onSelect={() => s().addChild(id)}>
            하위 작업 추가
            <ContextMenuShortcut>⇧{modKey}↩</ContextMenuShortcut>
          </ContextMenuItem>
          <ContextMenuItem onSelect={() => s().addSibling(id, { duration: 0, name: "마일스톤" })}>아래에 마일스톤 추가</ContextMenuItem>
          <ContextMenuSeparator />
          <ContextMenuItem onSelect={() => s().indent(selection)}>
            들여쓰기
            <ContextMenuShortcut>⇥</ContextMenuShortcut>
          </ContextMenuItem>
          <ContextMenuItem onSelect={() => s().outdent(selection)}>
            내어쓰기
            <ContextMenuShortcut>⇧⇥</ContextMenuShortcut>
          </ContextMenuItem>
          <ContextMenuItem disabled={many} onSelect={() => s().moveUp(id)}>
            위로 이동
            <ContextMenuShortcut>⌥↑</ContextMenuShortcut>
          </ContextMenuItem>
          <ContextMenuItem disabled={many} onSelect={() => s().moveDown(id)}>
            아래로 이동
            <ContextMenuShortcut>⌥↓</ContextMenuShortcut>
          </ContextMenuItem>
          <ContextMenuSeparator />
          <ContextMenuItem disabled={!many} onSelect={() => s().linkSelection()}>
            선택 작업 순서대로 연결 (FS)
          </ContextMenuItem>
          <ContextMenuItem onSelect={() => s().groupSelection()}>선택 작업을 새 단계로 묶기</ContextMenuItem>
        </>
      )}
      {surface.editProgress && (many || own.scheduleOwn(task)) && (
        <ContextMenuSub>
          <ContextMenuSubTrigger disabled={sched.isSummary && !many}>진척률</ContextMenuSubTrigger>
          <ContextMenuSubContent className="w-40">
            {[0, 25, 50, 75, 100].map((v) => (
              <ContextMenuCheckboxItem key={v} checked={!many && task.percentComplete === v} onSelect={() => setProgress(v)}>
                {v === 100 ? "완료 (100%)" : v === 0 ? "미착수 (0%)" : `${v}%`}
              </ContextMenuCheckboxItem>
            ))}
          </ContextMenuSubContent>
        </ContextMenuSub>
      )}
      {(surface.editWbs || surface.editProgress) && <ContextMenuSeparator />}
      <ContextMenuSub>
        <ContextMenuSubTrigger>Task</ContextMenuSubTrigger>
        <ContextMenuSubContent className="w-56">
          {surface.editTask && own.canCreateTask && (
            <ContextMenuItem onSelect={() => s().addExecutionTask({ linkedTaskIds: [id], start: sched.start, due: sched.finish })}>여기에 연결된 Task 만들기</ContextMenuItem>
          )}
          <ContextMenuItem disabled={linkedCount === 0} onSelect={() => openLinkedTasks(s().activeId ?? "", id)}>
            연결된 Task 보기{linkedCount ? ` (${linkedCount})` : ""}
          </ContextMenuItem>
        </ContextMenuSubContent>
      </ContextMenuSub>
      <ContextMenuSeparator />
      {sched.isSummary && <ContextMenuItem onSelect={() => s().toggleCollapse(id)}>{collapsed ? "펼치기" : "접기"}</ContextMenuItem>}
      <ContextMenuItem onSelect={() => s().scrollToDate(sched.start)}>간트에서 보기</ContextMenuItem>
      <ContextMenuItem
        onSelect={() => {
          s().select([id], id);
          s().setUI({ sidePanel: "inspector" });
        }}
      >
        인스펙터에서 열기
        <ContextMenuShortcut>{modKey}I</ContextMenuShortcut>
      </ContextMenuItem>
      <ContextMenuCheckboxItem checked={ui.highlightDrivingPath} onCheckedChange={(v) => s().setUI({ highlightDrivingPath: !!v })}>
        선택 작업의 날짜를 정한 경로 강조
      </ContextMenuCheckboxItem>
      {surface.editWbs && (
        <>
          <ContextMenuSeparator />
          <ContextMenuItem variant="destructive" onSelect={() => s().requestDeleteTasks(selection.length ? selection : [id])}>
            {many ? `${selection.length}개 작업 삭제` : "삭제"}
            <ContextMenuShortcut>{modKey}⌫</ContextMenuShortcut>
          </ContextMenuItem>
        </>
      )}
    </ContextMenuContent>
  );
}
